import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_array_includes, assert_instanceof, assert_string_not_empty, assert_type_number, not_implemented } from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { TYPE__class_constructor } from '@shared/types/TYPE__class_constructor';
import { UTILS__crypto } from '@shared/utils/crypto/UTILS__crypto';
import { cloneDeep, mergeWith } from 'es-toolkit';

export type DTO__base_excluded_keys =
  | '_id'
  | '_class_name_instance'
  | '_is_stub'
  | 'clone'
  | 'is_stub'
  | 'copy_fields'
  | 'get_hash_crc32'
  | 'assert_standard_fields'
  | 'on_assert_standard_fields_passed'
  | 'get_value_by_key'
  | 'fields_ignored_in_hash'
  | 'get_value_by_key_number'
  | 'hash_create'
  | 'get_flat_object_for_hash'
  | 'compare_fields';

export abstract class DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string;

  /*
   * STATIC METHODS
   */

  static CREATE_STUB<T extends DTO__base>(this: new () => T, overrides: Partial<T> = {}): T {
    return new this().clone({ ...overrides, _is_stub: true }) as T;
  }

  /*
   * FIELDS
   */
  _class_name_instance!: string;
  _is_stub!: boolean;

  get fields_ignored_in_hash(): Set<string> {
    // This local variable ensures you can't type a wrong field name here
    const out: Set<keyof this> = new Set();

    out.add('_class_name_instance');
    out.add('_is_stub');

    return out as Set<string>;
  }
  /*
   * EVENTS
   */

  on_assert_standard_fields_passed() {}

  /*
   * METHODS
   */

  constructor() {
    assert_string_not_empty((this.constructor as typeof DTO__base)._class_name_static, {
      context: CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_STATIC,
      assert_type: ENUM__error_type.SERIALIZATION_CLASSNAME_IS_EMPTY,
    });

    this._class_name_instance = (this.constructor as typeof DTO__base)._class_name_static;

    this._is_stub = false;
  }

  get_value_by_key_number(key: keyof this): number {
    let key_string = String(key);

    assert_array_includes(Object.keys(this), key_string);

    let out = this[key];

    assert_type_number(out);

    return out;
  }

  get_value_by_key<T>(key: keyof this, desired_type: TYPE__class_constructor<T>): T {
    let key_string = String(key);

    assert_array_includes(Object.keys(this), key_string);

    let out = this[key];

    assert_instanceof(out, desired_type);

    return out;
  }

  clone(overrides: Partial<this> = {}): this {
    let out = cloneDeep(this);

    //use clone to avoid using references of the previous object
    overrides = cloneDeep(overrides);

    // keep original class name instance
    (overrides as any)[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE] = this._class_name_instance;

    // 2. Deep merge with a customizer for arrays
    mergeWith(out, overrides, (value_destination, value_source) => {
      // If the source value (from overrides) is an array,
      // return it directly to overwrite the target value.
      if (Array.isArray(value_source)) return value_source;
      // If we return undefined, mergeWith falls back to default deep merge behavior
    });

    return out;
  }

  copy_fields<T extends this>(other: Partial<T>) {
    //use clone to avoid using references of the previous object
    other = cloneDeep(other);

    //keep original class name instance
    delete (other as any)[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE];

    Object.assign(this, other);
  }

  compare_fields(other: this): boolean {
    let out = true;

    let keys_other = Object.keys(other);

    for (let [key, value_this] of Object.entries(this)) {
      if (typeof value_this === 'function') continue;
      if (!keys_other.includes(key)) {
        out = false;
        break;
      }

      let value_other = (other as any)[key];

      if (value_this != value_other) {
        out = false;
        break;
      }
    }

    return out;
  }

  is_stub(): boolean {
    return this._is_stub;
  }

  toString() {
    let out = this.constructor.name;

    if (this.is_stub()) {
      out += ' STUB';
    }

    return out;
  }

  /*
   * HASH
   */
  hash_create() {
    let object_for_hash = this.get_flat_object_for_hash();

    return UTILS__crypto.sha256.from_object(object_for_hash);
  }

  get_flat_object_for_hash(prefix = '', result: Record<string, any> = {}) {
    UTILS__crypto.base.to_flat_object(this, prefix, result, this.fields_ignored_in_hash);

    return result;
  }

  /*
   * ASSERTS
   */
  assert_standard_fields(): void {
    not_implemented();
  }
}
